import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Pencil, Eye, ShieldAlert, Languages } from 'lucide-react';
import clsx from 'clsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSections, useSectionChapters } from '../hooks/useSections';
import { useChapter } from '../hooks/useChapter';
import { useAuth } from '../hooks/useAuth';
import { useChapterStatus } from '../hooks/useChapterStatus';
import { useLanguage } from '../hooks/useLanguage';
import { api } from '../lib/api';
import Breadcrumb from '../components/Breadcrumb';
import ChapterView from '../components/ChapterView';
import ChapterEditor from '../components/ChapterEditor';
import PermissionsPanel from '../components/PermissionsPanel';
import CommentsSection from '../components/CommentsSection';
import TableOfContents from '../components/TableOfContents';

export default function ChapterPage() {
  const { sectionSlug, chapterSlug, subSlug } = useParams();
  const { data: secData } = useSections();
  const section = secData?.sections.find((s) => s.slug === sectionSlug);
  const { data: chData } = useSectionChapters(section?.id);
  const { user } = useAuth();

  const chapter = useMemo(() => {
    if (!chData) return null;
    const top = chData.chapters.find((c) => c.slug === chapterSlug && !c.parentChapterId);
    if (!subSlug) return top;
    return chData.chapters.find((c) => c.slug === subSlug && c.parentChapterId === top?.id) ?? null;
  }, [chData, chapterSlug, subSlug]);

  const [mode, setMode] = useState<'draft' | 'published'>('published');
  const { data: full, isLoading } = useChapter(chapter?.id, mode);
  const { lang } = useLanguage();

  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const openTracker = useMutation({
    mutationFn: () => api.post('/reads/open', { chapter_id: chapter?.id }),
  });

  useEffect(() => {
    if (chapter?.id && mode === 'published') openTracker.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter?.id, mode]);

  if (!section) return <div>Sekce nenalezena.</div>;
  if (isLoading || !full) return <div className="text-sm text-gray-500">Načítám…</div>;

  const ch = full.chapter;

  // Pro EN reader: pokud máme přeloženou verzi, používáme ji. Jinak fallback CS + banner.
  const enAvailable = lang === 'en' && !!ch.contentEnMd && !!ch.titleEn;
  const displayTitle = enAvailable ? (ch.titleEn as string) : ch.titleCs;
  const displayContent = enAvailable ? (ch.contentEnMd as string) : ch.contentCsMd;

  const items: Array<{ label: string; href?: string }> = [
    { label: lang === 'en' ? 'Home' : 'Home', href: '/' },
    { label: (lang === 'en' && section.titleEn) || section.titleCs, href: `/s/${section.slug}` },
  ];
  if (subSlug) {
    const parent = chData?.chapters.find((c) => c.slug === chapterSlug && !c.parentChapterId);
    if (parent) items.push({ label: parent.draftTitleCs, href: `/c/${section.slug}/${chapterSlug}` });
  }
  items.push({ label: displayTitle });

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <Breadcrumb items={items} />
        {canEdit && <ModeToggle mode={mode} onChange={setMode} />}
      </div>

      {mode === 'draft' && canEdit ? (
        <ChapterEditor
          chapterId={ch.id}
          initialTitle={ch.titleCs}
          initialContent={ch.contentCsMd}
          initialRequiresAck={ch.requiresAcknowledgment}
        />
      ) : (
        <div className="toc:flex toc:gap-12">
          <article className="min-w-0 flex-1">
            <h1 className="mb-4 font-heading text-4xl font-bold text-dark">{displayTitle}</h1>
            {ch.requiresAcknowledgment && (
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold-soft px-3 py-1 text-xs font-semibold text-dark">
                <ShieldAlert className="h-3.5 w-3.5 text-gold" />
                {lang === 'en' ? 'Requires acknowledgement' : 'Vyžaduje potvrzení přečtení'}
              </div>
            )}
            {lang === 'en' && !enAvailable && (
              <div className="mb-6 flex items-center gap-2 rounded-md border border-teal/30 bg-teal-light px-3 py-2 text-xs text-dark">
                <Languages className="h-3.5 w-3.5 text-teal" />
                English translation is being prepared. Showing the Czech original meanwhile.
              </div>
            )}
            <ChapterView markdown={displayContent} />
            {ch.requiresAcknowledgment && ch.mode === 'published' && (
              <AcknowledgeBlock chapterId={ch.id} />
            )}
            <CommentsSection chapterId={ch.id} />
          </article>
          <TableOfContents />
        </div>
      )}

      {user?.role === 'admin' && (
        <PermissionsPanel scopeType="chapter" scopeId={ch.id} showBreakInheritance />
      )}
    </div>
  );
}

function ModeToggle({ mode, onChange }: { mode: 'draft' | 'published'; onChange: (m: 'draft' | 'published') => void }) {
  return (
    <div className="inline-flex shrink-0 rounded-md border border-gray-200 bg-white p-0.5 text-xs font-semibold">
      <button
        type="button"
        onClick={() => onChange('published')}
        className={clsx(
          'flex items-center gap-1.5 rounded px-3 py-1.5 transition-colors',
          mode === 'published' ? 'bg-dark text-white' : 'text-gray-500 hover:text-dark',
        )}
      >
        <Eye className="h-3.5 w-3.5" />
        Publikované
      </button>
      <button
        type="button"
        onClick={() => onChange('draft')}
        className={clsx(
          'flex items-center gap-1.5 rounded px-3 py-1.5 transition-colors',
          mode === 'draft' ? 'bg-dark text-white' : 'text-gray-500 hover:text-dark',
        )}
      >
        <Pencil className="h-3.5 w-3.5" />
        Draft
      </button>
    </div>
  );
}

function AcknowledgeBlock({ chapterId }: { chapterId: string }) {
  const qc = useQueryClient();
  const { data: statusData } = useChapterStatus();
  // Server je zdroj pravdy — UI volíme podle něj, ne podle lokálního „klikl jsem".
  const ackDoneServer = statusData?.statuses.find((s) => s.chapterId === chapterId)?.ackDone ?? false;
  const [optimisticAck, setOptimisticAck] = useState(false);
  const acked = ackDoneServer || optimisticAck;

  const m = useMutation({
    mutationFn: () => api.post('/reads/acknowledge', { chapter_id: chapterId }),
    onSuccess: () => {
      setOptimisticAck(true);
      qc.invalidateQueries({ queryKey: ['me', 'chapter-status'] });
    },
  });

  if (acked) {
    return (
      <div className="mt-10 flex items-center gap-2 rounded-md border border-positive/30 bg-positive-bg p-4 text-sm font-semibold text-positive">
        <Check className="h-5 w-5" /> Potvrzeno. Děkujeme.
      </div>
    );
  }

  return (
    <div className="mt-10 rounded-xl border border-gold/40 bg-gold-soft p-6 text-center">
      <div className="mb-3 font-heading text-xl font-bold text-dark">
        Tato kapitola vyžaduje potvrzení přečtení.
      </div>
      <p className="mb-5 text-sm text-gray-700">
        Potvrzením prohlašuješ, že jsi kapitolu přečetl/a a rozumíš jejímu obsahu.
      </p>
      <button className="btn-primary" disabled={m.isPending} onClick={() => m.mutate()}>
        <Check className="h-4 w-4" />
        {m.isPending ? 'Zaznamenávám…' : 'Přečetl/a jsem a rozumím'}
      </button>
    </div>
  );
}
